import { Box, Divider } from "@mui/material";
import React, { useState } from "react";

import { useReviewPageHotkeys } from "hooks/useHotkeys/useHotkeys";
import { CountButton } from "new-components/buttons/CountButton";
import { Typography } from "new-components/typography/Typography";
import { DetectorModes } from "types/createDetector";
import { CanvasAnnotation } from "types/imageAnnotations";
import { Posicheck } from "types/imageQueries";

import { UnclearActionsRow } from "./UnclearActionsRow";

export interface CountImageActionsProps {
  imageQueryId?: Posicheck["id"];
  isUnclearMenuOpen: boolean;
  setUnclearMenuOpen: (isOpen: boolean) => void;
  onUnclearSubmit: (feedbackText: string) => void;
  maxCount: number;
  onLabelSubmit: (value: number | string, annotations?: CanvasAnnotation[]) => void;
  annotations?: CanvasAnnotation[];
  isCountMode?: boolean;
  showDeleteButton?: boolean;
  onDeleteImageQuery?: () => void | Promise<void>;
  isDeletingImageQuery?: boolean;
}

export function CountImageActions(props: CountImageActionsProps) {
  const {
    imageQueryId,
    isUnclearMenuOpen,
    setUnclearMenuOpen,
    onUnclearSubmit,
    maxCount,
    onLabelSubmit,
    annotations,
    isCountMode = true,
    showDeleteButton,
    onDeleteImageQuery,
    isDeletingImageQuery,
  } = props;

  const value = annotations?.length ?? 0;
  const [valueError, setValueError] = useState<string>("");
  const hasImageQuery = Boolean(imageQueryId);

  const { useUnclearHotkey, useSubmitCountHotkey } = useReviewPageHotkeys({
    detectorMode: DetectorModes.COUNT,
    isUnclearMenuOpen,
    setUnclearMenuOpen,
    hasImageQuery,
    onLabelSubmit,
    annotations,
    valueError,
    setValueError,
    isCountMode,
    maxCount,
  });

  const handleCountSubmit = () => {
    if (value === undefined) {
      setValueError("The count value is required");
      return;
    }

    if (valueError) return;
    if (isCountMode) {
      onLabelSubmit(value, annotations);
    } else {
      let bboxesValue = "NO_OBJECTS";
      if (value > 0) {
        bboxesValue = "BOUNDING_BOX";
      }
      if (value > (maxCount ?? 0)) {
        bboxesValue = "GREATER_THAN_MAX";
      }
      onLabelSubmit(bboxesValue, annotations);
    }
  };

  useUnclearHotkey();
  useSubmitCountHotkey();

  return (
    <Box id="count-actions" display="flex" className="bg-[#E5E9ED] rounded mx-[43px] my-[32px] items-center">
      <Box id="count-actions-options" height="100%" flexGrow={1} position="relative">
        <CountButton maxCount={maxCount} handleCountSubmit={handleCountSubmit} value={value} />
        {maxCount && value > maxCount && (
          <Typography
            fontWeight="semibold"
            noWrap
            sx={{
              position: "absolute",
              bottom: "-27.5px",
              left: "50%",
              transform: "translateX(-50%)",
              color: "error.main",
            }}
          >
            Maximum object count reached
          </Typography>
        )}
      </Box>
      <Divider
        id="count-actions-divider"
        orientation="vertical"
        flexItem
        sx={{
          marginX: "20px",
          height: "36px",
          borderColor: "#000000",
        }}
      />
      <Box id="count-actions-unclear" flexShrink={0}>
        <UnclearActionsRow
          hasImageQuery={hasImageQuery}
          isUnclearMenuOpen={isUnclearMenuOpen}
          setUnclearMenuOpen={setUnclearMenuOpen}
          onUnclearSubmit={onUnclearSubmit}
          showDeleteButton={showDeleteButton}
          onDeleteImageQuery={onDeleteImageQuery}
          isDeletingImageQuery={isDeletingImageQuery}
        />
      </Box>
    </Box>
  );
}
